/**
 * THE SIDE PANEL (B7): the dusk-glass panel on the left (desktop, compact,
 * large, kiosk) and the same content inside B10's mobile bottom sheet.
 * docs/design-spec.md §A, §B, §E.4, §E.9, §F, §H.
 *
 *   <PlacesPanel />                       desktop / kiosk: positions itself (left, top, bottom per §A)
 *   <PlacesPanel embedded />              inside the mobile sheet: no glass or position of its own,
 *                                         compact header (groups and chips first, then search and list),
 *                                         fills its parent (height: 100%)
 *   <PlacesPanel onHome={() => …} />      the logo's home(): the panel clears selection, category and
 *                                         bloom in the store; the bridge moves the camera home
 *
 * Reads and writes the store only; no map calls. Hover previews, selection and
 * bloom reach the map through the store (hover, select, bloom), which the bridge
 * turns into Choreographer calls.
 *
 * Phase: hidden (and inert) while loading and during the intro, arrives with
 * .ld-ui-in (640 settle, rise 12 px) when the phase becomes "explore"; dims to
 * 0.35 and goes inert during a drive; inert during kiosk attract.
 */
import "@/design";
import "./panel.css";
import { useCallback, useEffect, useMemo, useRef, useState, type JSX, type MouseEvent } from "react";
import { Button, cx, Logo, useReducedMotion } from "@/design";
import type { Category, GroupId } from "@/contracts/data";
import type { SortKey } from "@/contracts/store";
import { GROUPS } from "@/contracts/taxonomy";
import { openAbout } from "@/features/hud/aboutSignal";
import { announce } from "@/store/bus";
import { selectCategoriesForGroup, selectVisiblePlaces } from "@/store/selectors";
import { actions, getApp, useApp } from "@/store/store";
import { Chips } from "./Chips";
import { countLabel, COPY, groupAnnouncement, groupIndexForKey, isTextTarget, listKey as makeListKey, listLabel } from "./format";
import { GroupControl, type PanelMode } from "./GroupControl";
import { PlaceList } from "./PlaceList";
import { SearchField } from "./SearchField";
import { SortMenu } from "./SortMenu";

export interface PlacesPanelProps {
  /** true inside B10's mobile bottom sheet (compact header, no positioning or glass of its own). */
  embedded?: boolean;
  /** Logo → home(): runs after the panel has cleared selection, category and bloom (the bridge moves the camera). */
  onHome?: () => void;
  className?: string;
}

const GROUP_BY_ID = new Map(GROUPS.map((g) => [g.id, g]));

/** The listbox's id: the target of the "Skip to places" link (§H.1). */
export const PLACES_LISTBOX_ID = "ld-places";

export function PlacesPanel({ embedded = false, onHome, className }: PlacesPanelProps = {}): JSX.Element {
  const rootRef = useRef<HTMLElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Stable: B10's "Skip to places" link targets it (§H.1). One panel per page, as the option ids (ld-opt-{id}) assume.
  const listId = PLACES_LISTBOX_ID;

  const data = useApp((s) => s.data);
  const geo = useApp((s) => s.geo);
  const group = useApp((s) => s.group);
  const category = useApp((s) => s.category);
  const query = useApp((s) => s.query);
  const sort = useApp((s) => s.sort);
  const selectedId = useApp((s) => s.selectedId);
  const layout = useApp((s) => s.layout);
  // Derived flags, not the phase itself: starting a drive re-renders nothing here (the layout root carries
  // data-phase="drive", panel.css dims from it and the layout makes the panel slot inert; QA L2-06).
  const away = useApp((s) => s.phase === "loading" || s.phase === "intro");
  const asleep = useApp((s) => s.phase === "attract");
  // The raking light follows the pointer through the app root's useLightField (one per root,
  // installed by the shell, as for the card and the HUD); the panel does not add a second one.
  const reduced = useReducedMotion();

  const compact = embedded || layout === "mobile";
  const kiosk = layout === "kiosk";
  const mode: PanelMode = compact ? "mobile" : kiosk ? "kiosk" : "desktop";
  const touch = compact || kiosk;
  const loading = !data;

  /* ── derived lists (memoised on the state they read) ──────────────── */
  const categories = useMemo(() => (data ? selectCategoriesForGroup(getApp()) : []), [data, group]); // eslint-disable-line react-hooks/exhaustive-deps
  const places = useMemo(() => (data ? selectVisiblePlaces(getApp()) : []), [data, group, category, query, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const categoryMap = useMemo(() => new Map<string, Category>((data?.categories ?? []).map((c) => [c.id, c])), [data]);
  const roadRefs = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of geo?.roads.features ?? []) if (f.properties.ref) m.set(f.properties.id, f.properties.ref);
    return m;
  }, [geo]);

  const searching = query.trim() !== "";
  const grouped = !category && !searching;
  const total = categories.reduce((n, c) => n + c.count, 0);
  const groupDef = GROUP_BY_ID.get(group) ?? GROUPS[0];
  const activeCat = category ? categoryMap.get(category) : undefined;
  const listName = listLabel(searching ? COPY.searchResults : (activeCat?.label ?? groupDef.label), places.length);
  const key = makeListKey({ group, category, sort, searching });

  /* ── phase: arrival and attract (the drive is dimmed from the layout root) ── */
  const inert = away || asleep;
  const [arrival, setArrival] = useState(0);
  const [wasAway, setWasAway] = useState(away);
  if (wasAway !== away) {
    setWasAway(away);
    if (!away) setArrival((n) => n + 1);
  }

  /* ── actions ──────────────────────────────────────────────────────── */
  const chooseGroup = useCallback((g: GroupId) => {
    const s = getApp();
    if (s.query) actions.setQuery("");
    if (g === s.group && !s.category) return;
    actions.setGroup(g);
    const after = getApp();
    if (after.phase !== "attract") announce(groupAnnouncement(GROUP_BY_ID.get(g)?.label ?? g, selectVisiblePlaces(after).length));
  }, []);

  const pressChip = useCallback((id: string | null) => {
    const s = getApp();
    if (s.query) actions.setQuery("");
    if (id === null || id === s.category) {
      actions.setCategory(null);
      actions.bloom(null);
    } else {
      actions.setCategory(id);
      actions.bloom(id);
    }
  }, []);

  const goHome = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    actions.select(null);
    actions.setCategory(null);
    actions.bloom(null);
    onHome?.();
  };

  const clearSearch = useCallback(() => {
    actions.setQuery("");
    searchRef.current?.focus();
  }, []);

  const onSort = useCallback((s: SortKey) => actions.setSort(s), []);

  /* ── global keys: "/" focuses search, 1–4 choose a group (§B.3) ──── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      if (isTextTarget(e.target as HTMLElement | null)) return;
      const s = getApp();
      if (s.phase !== "explore" || !s.data) return;
      const active = document.activeElement;
      if (active instanceof Element && active.closest('[aria-modal="true"], [role="menu"]')) return;
      if (e.key === "/") {
        e.preventDefault();
        if (embedded && s.sheet === "peek") actions.setSheet("half");
        searchRef.current?.focus();
        return;
      }
      const gi = groupIndexForKey(e.key);
      if (gi != null && GROUPS[gi]) chooseGroup(GROUPS[gi].id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chooseGroup, embedded]);

  /* ── keyboard mode: focus rings on rows and the field only for keys ── */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const kb = (e: KeyboardEvent) => {
      if (e.key === "Tab" || e.key.startsWith("Arrow") || e.key === "Home" || e.key === "End" || e.key === "PageUp" || e.key === "PageDown") root.setAttribute("data-kb", "");
    };
    const ptr = () => root.removeAttribute("data-kb");
    window.addEventListener("keydown", kb, true);
    window.addEventListener("pointerdown", ptr, true);
    return () => {
      window.removeEventListener("keydown", kb, true);
      window.removeEventListener("pointerdown", ptr, true);
    };
  }, []);

  /* ── render ───────────────────────────────────────────────────────── */
  const endnote = compact ? (
    <div className="ld-loc-panel-endnote">
      <Button variant="quiet" size="sm" onClick={(e) => openAbout(e.currentTarget)}>
        {COPY.about}
      </Button>
      <p className="ld-loc-panel-attrib">{COPY.attribution}</p>
      <p className="ld-loc-panel-powered">
        Powered by <b>Flux Real</b>
      </p>
    </div>
  ) : null;

  return (
    <aside
      ref={rootRef}
      aria-label={COPY.aria}
      inert={inert}
      data-motion={reduced ? "reduced" : undefined}
      data-layout={mode}
      className={cx(
        "ld-loc-panel ld-ui-in",
        embedded ? "ld-loc-panel--embedded" : "ld-glass",
        compact && "ld-loc-panel--compact",
        kiosk && "ld-loc-panel--kiosk",
        touch && "ld-loc-panel--touch",
        away && "is-away",
        className,
      )}
    >
      {!compact && (
        <header className="ld-loc-panel-head">
          <Logo size={kiosk ? 48 : 40} href="#" onClick={goHome} className="ld-loc-panel-logo" />
          <p className="ld-meta ld-loc-panel-kicker">{COPY.kicker}</p>
          <h2 className="ld-h3 ld-loc-panel-title">{COPY.title}</h2>
          <p className="ld-loc-panel-sub">{COPY.sub}</p>
        </header>
      )}

      <GroupControl value={group} onChange={chooseGroup} mode={mode} reduced={reduced} />

      <Chips categories={categories} value={category} groupLabel={groupDef.label} total={total} onPress={pressChip} reduced={reduced} loading={loading} />

      <SearchField inputRef={searchRef} listId={listId} embedded={embedded} disabled={loading} />

      <div className="ld-loc-panel-lhead">
        <p className="ld-loc-panel-count">
          <span className="ld-meta ld-num">{loading ? " " : countLabel(places.length)}</span>
          {compact && <span className="ld-meta ld-loc-panel-legend">{COPY.offPeak}</span>}
        </p>
        <SortMenu value={sort} onChange={onSort} reduced={reduced} disabled={loading} />
      </div>

      <PlaceList
        id={listId}
        places={places}
        listKey={key}
        grouped={grouped}
        searching={searching}
        query={query}
        sort={sort}
        selectedId={selectedId}
        categories={categoryMap}
        roadRefs={roadRefs}
        label={listName}
        hoverPreview={!touch}
        reduced={reduced}
        loading={loading}
        arrival={arrival}
        onClearSearch={clearSearch}
        endnote={endnote}
        scrollRef={scrollRef}
      />

      {!compact && (
        <footer className="ld-loc-panel-foot">
          <Button variant="quiet" size="sm" onClick={(e) => openAbout(e.currentTarget)}>
            {COPY.about}
          </Button>
        </footer>
      )}
    </aside>
  );
}
